import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

const SIZE_CLASS = {
  sm: "max-w-md",
  md: "max-w-lg",
  lg: "max-w-3xl",
};

/** How long `leaving` takes to play out; matches `animate-modal-panel-out`. */
export const MODAL_EXIT_MS = 180;

/**
 * The caller's half of `Modal`'s exit motion, for a dialog mounted per open.
 *
 * `leaveThen(done)` plays the exit and calls `done` once it has finished; the
 * first way out wins, so a second click mid-exit does nothing. `close` is
 * `leaveThen(onClose)`, stable across renders, and refuses while `blocked` —
 * a write in flight — so Cancel, ×, Escape and the backdrop can't drop it.
 *
 * @param {Object} params
 * @param {() => void} params.onClose
 * @param {boolean} [params.blocked=false]
 * @returns {{ leaving: boolean, close: () => void, leaveThen: (done: () => void) => void }}
 */
export function useModalExit({ onClose, blocked = false }) {
  const [leaving, setLeaving] = useState(false);
  const leavingRef = useRef(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const leaveThen = useCallback((done) => {
    if (leavingRef.current) return;
    leavingRef.current = true;
    setLeaving(true);
    timer.current = setTimeout(done, MODAL_EXIT_MS);
  }, []);

  // Refs, so `close` stays stable and `Modal` doesn't re-run its focus effect.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const blockedRef = useRef(blocked);
  blockedRef.current = blocked;
  const close = useCallback(() => {
    if (blockedRef.current) return;
    leaveThen(() => onCloseRef.current());
  }, [leaveThen]);

  return { leaving, close, leaveThen };
}

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * A centred dialog: title, optional subtitle, a close ×, a scrolling body and an
 * optional footer.
 *
 * The repo had no shared one — every modal hand-rolled `role="dialog"`, its own
 * Escape and outside-click listeners and its own scroll lock — so this is that
 * shell, once. It owns none of the content's behaviour: a form inside it
 * submits, validates and closes itself.
 *
 * What it does own:
 * - **Escape and a backdrop click close it**, via `onClose`. Only a press on the
 *   backdrop itself counts, so a popover the content portals to `document.body`
 *   (a time picker, say) can be clicked without closing the dialog. Never stack
 *   two of these (or one of these over a `Drawer`) at once.
 * - **Focus**: moves into the panel on open (the first focusable element, or
 *   the panel itself), keeps Tab within it, and returns to whatever had focus
 *   before on close.
 * - **Body scroll lock** while open.
 *
 * Renders nothing when closed, so a caller can keep it mounted.
 *
 * **Portalled to `document.body`**, above the app shell rather than inside
 * the page's scrolling `<main>`, so the backdrop always covers the whole
 * viewport, top edge included, whatever the page's own layers do.
 *
 * **Motion** is opt-in: `animated` fades the backdrop and lifts the panel in on
 * mount, and `leaving` plays the reverse. The exit needs the caller's help —
 * this unmounts the moment `isOpen` drops — so a caller sets `leaving`, waits
 * `MODAL_EXIT_MS`, then closes — `useModalExit` does that. Both honour
 * reduced motion.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {React.ReactNode} props.title
 * @param {React.ReactNode} [props.subtitle]
 * @param {React.ReactNode} [props.footer] - Pinned under the body, e.g. buttons.
 * @param {"sm"|"md"|"lg"} [props.size="sm"]
 * @param {string} [props.ariaLabel] - Overrides the accessible name, which is
 *   otherwise the title.
 * @param {boolean} [props.animated=false] - Animate in on mount.
 * @param {boolean} [props.leaving=false] - Play the exit animation.
 * @param {boolean} [props.closeDisabled=false] - Grey out the close ×, e.g.
 *   while a write is in flight. Escape and the backdrop still call `onClose`;
 *   the caller decides whether to honour them then.
 * @param {React.ReactNode} props.children
 */
export const Modal = ({
  isOpen,
  onClose,
  title,
  subtitle,
  footer,
  size = "sm",
  ariaLabel,
  animated = false,
  leaving = false,
  closeDisabled = false,
  children,
}) => {
  const overlayRef = useRef(null);
  const panelRef = useRef(null);
  const titleId = useId();

  useEffect(() => {
    if (!isOpen) return undefined;

    const previouslyFocused = document.activeElement;
    const panel = panelRef.current;
    const first = panel?.querySelector(FOCUSABLE);
    // preventScroll: focusing must not scroll the page behind the backdrop.
    (first || panel)?.focus({ preventScroll: true });

    const handleOutside = (event) => {
      const overlay = overlayRef.current;
      const panel = panelRef.current;
      if (overlay && panel && overlay.contains(event.target) && !panel.contains(event.target)) onClose();
    };
    const handleKey = (event) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab" || !panelRef.current) return;
      const focusable = [...panelRef.current.querySelectorAll(FOCUSABLE)];
      if (focusable.length === 0) return;
      const firstEl = focusable[0];
      const lastEl = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === firstEl) {
        event.preventDefault();
        lastEl.focus();
      } else if (!event.shiftKey && document.activeElement === lastEl) {
        event.preventDefault();
        firstEl.focus();
      }
    };

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("keydown", handleKey);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("keydown", handleKey);
      document.body.style.overflow = previousOverflow;
      if (previouslyFocused && typeof previouslyFocused.focus === "function") {
        previouslyFocused.focus({ preventScroll: true });
      }
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const overlayMotion = leaving
    ? "pointer-events-none motion-safe:animate-modal-overlay-out motion-reduce:opacity-0"
    : animated
    ? "motion-safe:animate-modal-overlay-in"
    : "";
  const panelMotion = leaving
    ? "motion-safe:animate-modal-panel-out"
    : animated
    ? "motion-safe:animate-modal-panel-in"
    : "";

  return createPortal(
    <div
      ref={overlayRef}
      className={`fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/40 p-4 sm:items-center ${overlayMotion}`}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel}
        aria-labelledby={ariaLabel ? undefined : titleId}
        tabIndex={-1}
        className={`flex max-h-[calc(100vh-2rem)] w-full ${
          SIZE_CLASS[size] || SIZE_CLASS.sm
        } flex-col rounded-xl bg-white shadow-xl focus:outline-none ${panelMotion}`}
      >
        <div className="flex flex-shrink-0 items-start justify-between gap-3 border-b border-bc-hairline px-5 py-4">
          <div className="min-w-0">
            <h2 id={titleId} className="text-base font-bold text-bc-text">
              {title}
            </h2>
            {subtitle ? <p className="mt-0.5 text-xs text-bc-soft">{subtitle}</p> : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={closeDisabled}
            aria-label="Close"
            className="inline-flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg bg-light-gray/60 text-bc-text-2 transition-colors hover:bg-light-gray disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:bg-light-gray/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-primary"
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 py-4">{children}</div>

        {footer ? (
          <div className="flex flex-shrink-0 flex-wrap items-center justify-end gap-2 border-t border-bc-hairline px-5 py-4">
            {footer}
          </div>
        ) : null}
      </div>
    </div>,
    document.body
  );
};
